/**
 * @override King Living — chromeless Vimeo: oEmbed query uses 0/1 flags; iframe src patched before @vimeo/player init.
 * Keeps vendor `vendors/pwa-base` unchanged; this file overlays via ccExtensibility resolution.
 */

import BaseVimeoPlayer from '@vimeo/player';

import {Player} from '@amblique/base/app/plugins/video/adapters/player';
import {VimeoPlayer} from '@amblique/base/app/plugins/video/adapters/vimeo-player';
import {Vimeo as BaseVimeo} from '@amblique/base/app/plugins/video/providers/vimeo';

export class Vimeo extends BaseVimeo {
  /** Lets `texttrack` flow into `options.params` (base Vimeo PARAMS omit it). @see https://github.com/vimeo/player.js#embed-options */
  static PARAMS = [...BaseVimeo.PARAMS, 'texttrack'];

  /**
   * @protected
   */
  get oembedUrl() {
    const videoUrl = `https://vimeo.com/${this.id}`;
    const oembedUrl = 'https://vimeo.com/api/oembed.json';
    const params = new URLSearchParams();

    params.set('url', videoUrl);

    for (const [key, value] of Object.entries(this.options.params || {})) {
      if (key === 'url') {
        continue;
      }

      if (value === false) {
        params.set(key, '0');
      } else if (value === true) {
        params.set(key, '1');
      } else if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    }

    return `${oembedUrl}?${params.toString()}`;
  }

  /**
   * @protected
   */
  createPlayer(metadata) {
    const div = document.createElement('div');

    div.innerHTML = metadata.metadata.html.replace(' allowfullscreen ', ' ');
    const root = div.firstChild;

    this.applyClip(root);
    this.element.appendChild(root);

    const iframe = root?.tagName === 'IFRAME' ? root : root?.querySelector?.('iframe');

    if (iframe?.src) {
      try {
        const url = new URL(iframe.src);

        if (this.options.params.controls === false || this.options.params.controls === 0) {
          url.searchParams.set('controls', '0');
        }

        if (this.options.params.background === true || this.options.params.background === 1) {
          url.searchParams.set('background', '1');
        }

        if (this.options.params.texttrack) {
          url.searchParams.set('texttrack', String(this.options.params.texttrack));
        }

        iframe.src = url.toString();
      } catch {
        // Ignore invalid iframe URLs
      }
    }

    const nativePlayer = new BaseVimeoPlayer(this.element, this.options.params);
    const trackLang = this.options.params.texttrack;

    if (trackLang && typeof nativePlayer.enableTextTrack === 'function') {
      nativePlayer.ready().then(() => nativePlayer.enableTextTrack(trackLang).catch(() => {}));
    }

    const vimeoPlayerAdapter = new VimeoPlayer(nativePlayer);

    this.player = new Player(vimeoPlayerAdapter);

    return this.player;
  }
}
